import * as layout from '../../../components/markLayout.module.css';
import { MarkButton, MarkHeading } from '../../../components/MarkControls';
import React, { useCallback, useId, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import useCreateDrip from '../../../api/useCreateDrip';
import useUnsavedChangesRegistration from '../../../hooks/useUnsavedChangesRegistration';
import Step1Identity from './Step1Identity';
import Step2Audience from './Step2Audience';
import Step3Schedule from './Step3Schedule';
import Step4Review from './Step4Review';
import type { Step1Data } from './Step1Identity';
import type { Step2Data } from './Step2Audience';
import type { Step3Data } from './Step3Schedule';
import { DRIP_ERROR_LABELS } from '../../../constants';
import type { AxiosError } from 'axios';
const STEPS = ['Identity', 'Audience', 'Schedule', 'Review'] as const;
const extractErrorMessage = (error: AxiosError): string => {
    const data = error.response?.data as Record<string, unknown> | undefined;
    if (data?.errors) {
        const codes = Array.isArray(data.errors) ? data.errors : [data.errors];
        const labels = codes
            .filter((c): c is string => typeof c === 'string')
            .map((c) => DRIP_ERROR_LABELS[c] ?? c);
        if (labels.length) return labels.join('. ');
    }
    if (data?.error && typeof data.error === 'string') {
        return DRIP_ERROR_LABELS[data.error] ?? data.error;
    }
    if (data?.detail && typeof data.detail === 'string') return data.detail;
    return error.message || 'Create failed.';
};
const NewDrip: React.FC = () => {
    const navigate = useNavigate();
    const createDrip = useCreateDrip();
    const stepNavId = useId();
    const [dirty, setDirty] = useState(false);
    const [step, setStep] = useState(0);
    const [furthestStep, setFurthestStep] = useState(0);
    const [step1, setStep1] = useState<Step1Data>({
        name: '',
        triggerType: 'PROSPECT',
        prospectType: 'D2C'
    });
    const [step2, setStep2] = useState<Step2Data>({
        enrollmentSegmentId: null,
        enrollmentSegmentName: null,
        reEntryPolicy: 'once_ever',
        reEntryDays: 30
    });
    const [step3, setStep3] = useState<Step3Data>({
        sendWindowStart: '',
        sendWindowEnd: ''
    });
    const [step1Errors, setStep1Errors] = useState<string[]>([]);
    const [step2Errors, setStep2Errors] = useState<string[]>([]);
    const [step3Errors, setStep3Errors] = useState<string[]>([]);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const isSaving = createDrip.isPending;
    const unregister = useUnsavedChangesRegistration('drip-create', dirty, isSaving);
    const stepRefs = useRef<(HTMLDivElement | null)[]>([null, null, null, null]);
    const stepHeadingRefs = useRef<(HTMLHeadingElement | null)[]>([null, null, null, null]);
    const submittingRef = useRef(false);
    const focusStep = (index: number) => {
        requestAnimationFrame(() => stepHeadingRefs.current[index]?.focus());
    };
    const focusField = (index: number, field?: string | null) => {
        requestAnimationFrame(() => {
            const container = stepRefs.current[index];
            if (!container) return;
            if (field) {
                const target = container.querySelector<HTMLElement>(`[data-field="${field}"]`);
                if (target) {
                    target.focus();
                    return;
                }
            }
            const fallback = container.querySelector<HTMLElement>(
                'input:not(:disabled), select:not(:disabled), textarea:not(:disabled)'
            );
            if (fallback) {
                fallback.focus();
            } else {
                stepHeadingRefs.current[index]?.focus();
            }
        });
    };
    const handleStep1Change = useCallback((data: Step1Data) => {
        setDirty(true);
        setStep1(data);
        setStep1Errors([]);
    }, []);
    const handleStep2Change = useCallback((data: Step2Data) => {
        setDirty(true);
        setStep2(data);
        setStep2Errors([]);
    }, []);
    const handleStep3Change = useCallback((data: Step3Data) => {
        setDirty(true);
        setStep3(data);
        setStep3Errors([]);
    }, []);
    const validateStep1 = (): {
        errors: string[];
        focus: string | null;
    } => {
        const errs: string[] = [];
        let focus: string | null = null;
        if (!step1.name.trim()) {
            errs.push('Name is required.');
            focus ??= 'name';
        }
        setStep1Errors(errs);
        return { errors: errs, focus };
    };
    const validateStep2 = (): {
        errors: string[];
        focus: string | null;
    } => {
        const errs: string[] = [];
        const focus: string | null = null;
        if (
            step1.triggerType === 'SEGMENT' &&
            step2.reEntryPolicy === 'once_per_n_days' &&
            step2.reEntryDays < 1
        ) {
            errs.push('Re-entry days must be at least 1.');
        }
        setStep2Errors(errs);
        return { errors: errs, focus };
    };
    const validateStep3 = (): {
        errors: string[];
        focus: string | null;
    } => {
        const errs: string[] = [];
        let focus: string | null = null;
        const hasStart = !!step3.sendWindowStart;
        const hasEnd = !!step3.sendWindowEnd;
        if (hasStart !== hasEnd) {
            errs.push('Both send window start and end must be set, or both empty.');
            focus ??= hasStart ? 'send-window-end' : 'send-window-start';
        } else if (hasStart && hasEnd) {
            const timePattern = /^([01]\d|2[0-3]):[0-5]\d$/;
            const validStart = timePattern.test(step3.sendWindowStart);
            const validEnd = timePattern.test(step3.sendWindowEnd);
            if (!validStart) {
                errs.push('Enter a send window start in HH:MM format (00:00–23:59).');
                focus ??= 'send-window-start';
            }
            if (!validEnd) {
                errs.push('Enter a send window end in HH:MM format (00:00–23:59).');
                focus ??= 'send-window-end';
            }
            if (validStart && validEnd && step3.sendWindowStart >= step3.sendWindowEnd) {
                errs.push('Send window start must be before end.');
                focus ??= 'send-window-start';
            }
        }
        setStep3Errors(errs);
        return { errors: errs, focus };
    };
    const goNext = () => {
        if (isSaving) return;
        if (step === 0) {
            const v = validateStep1();
            if (v.errors.length > 0) {
                focusField(0, v.focus);
                return;
            }
        }
        if (step === 1) {
            const v = validateStep2();
            if (v.errors.length > 0) {
                focusField(1, v.focus);
                return;
            }
        }
        if (step === 2) {
            const v = validateStep3();
            if (v.errors.length > 0) {
                focusField(2, v.focus);
                return;
            }
        }
        const next = Math.min(step + 1, STEPS.length - 1);
        setStep(next);
        setFurthestStep((current) => Math.max(current, next));
        focusStep(next);
    };
    const goPrev = () => {
        if (isSaving) return;
        const prev = Math.max(step - 1, 0);
        setStep(prev);
        focusStep(prev);
    };
    const goToStep = (target: number, focusHeading = true) => {
        if (isSaving || target > furthestStep) return;
        if (target > 0) {
            const validation = validateStep1();
            if (validation.errors.length > 0) {
                setStep(0);
                focusField(0, validation.focus);
                return;
            }
        }
        setStep(target);
        if (focusHeading) focusStep(target);
    };
    const handleStepKeyDown = (event: React.KeyboardEvent, index: number) => {
        let target: number | null = null;
        if (event.key === 'ArrowRight') target = Math.min(index + 1, furthestStep);
        if (event.key === 'ArrowLeft') target = Math.max(index - 1, 0);
        if (target === null || target === index) return;
        event.preventDefault();
        goToStep(target, false);
        const el = document.getElementById(`${stepNavId}-step-${target}`);
        el?.focus();
    };
    const handleSubmit = async () => {
        if (submittingRef.current) return;
        const v1 = validateStep1();
        const v2 = validateStep2();
        const v3 = validateStep3();
        if (v1.errors.length > 0 || v2.errors.length > 0 || v3.errors.length > 0) {
            if (v1.errors.length > 0) {
                setStep(0);
                focusField(0, v1.focus);
            } else if (v2.errors.length > 0) {
                setStep(1);
                focusField(1, v2.focus);
            } else {
                setStep(2);
                focusField(2, v3.focus);
            }
            return;
        }
        submittingRef.current = true;
        setSubmitError(null);
        try {
            const response = await createDrip.mutateAsync({
                name: step1.name.trim(),
                trigger_type: step1.triggerType,
                ...(step1.triggerType === 'PROSPECT' && {
                    prospect_type: step1.prospectType
                }),
                ...(step1.triggerType === 'SEGMENT' &&
                    step2.enrollmentSegmentId != null && {
                        enrollment_segment_id: step2.enrollmentSegmentId
                    }),
                ...(step1.triggerType === 'SEGMENT' && {
                    re_entry_policy: step2.reEntryPolicy,
                    ...(step2.reEntryPolicy === 'once_per_n_days' && {
                        re_entry_days: step2.reEntryDays
                    })
                }),
                ...(step3.sendWindowStart &&
                    step3.sendWindowEnd && {
                        send_window_start: step3.sendWindowStart,
                        send_window_end: step3.sendWindowEnd
                    })
            });
            flushSync(unregister);
            navigate(`/mark/drips/${response.data.drip_id}`);
        } catch (error) {
            setSubmitError(extractErrorMessage(error as AxiosError));
        } finally {
            submittingRef.current = false;
        }
    };
    return (
        <Section subtheme={['regular', 'background']} className={layout.form} noDefaultPadding>
            <H role="h4">New drip</H>
            <P noDefaultMargin styles={{ color: 'var(--mark-muted)' }}>
                Set up enrollment and delivery rules. Creation saves a draft so you can build and
                review its journey before activation.
            </P>
            <nav aria-label="Wizard steps">
                <ol
                    role="list"
                    style={{
                        listStyle: 'none',
                        margin: '0 0 1.6rem 0'
                    }}
                    className={layout.steps}
                >
                    {STEPS.map((label, i) => {
                        const isActive = i === step;
                        const isReachable = i <= furthestStep;
                        return (
                            <li key={label}>
                                <MarkButton
                                    type="button"
                                    id={`${stepNavId}-step-${i}`}
                                    aria-current={isActive ? 'step' : undefined}
                                    data-reachable={isReachable}
                                    disabled={!isReachable || isSaving}
                                    onClick={() => goToStep(i)}
                                    onKeyDown={(e) => handleStepKeyDown(e, i)}
                                    className={layout.stepButton}
                                    size="regular"
                                    style={{
                                        width: '100%',
                                        textAlign: 'left',
                                        whiteSpace: 'normal'
                                    }}
                                >
                                    {i + 1}. {label}
                                </MarkButton>
                            </li>
                        );
                    })}
                </ol>
            </nav>
            <fieldset
                disabled={isSaving}
                style={{
                    margin: 0
                }}
                className={layout.fieldset}
            >
                {step === 0 && (
                    <div
                        ref={(el) => {
                            stepRefs.current[0] = el;
                        }}
                    >
                        <MarkHeading
                            role="h4"
                            ref={(el) => {
                                stepHeadingRefs.current[0] = el;
                            }}
                            tabIndex={-1}
                            style={{
                                margin: '0 0 1rem'
                            }}
                        >
                            Identity
                        </MarkHeading>
                        <Step1Identity
                            data={step1}
                            onChange={handleStep1Change}
                            errors={step1Errors}
                        />
                    </div>
                )}
                {step === 1 && (
                    <div
                        ref={(el) => {
                            stepRefs.current[1] = el;
                        }}
                    >
                        <MarkHeading
                            role="h4"
                            ref={(el) => {
                                stepHeadingRefs.current[1] = el;
                            }}
                            tabIndex={-1}
                            style={{
                                margin: '0 0 1rem'
                            }}
                        >
                            Audience
                        </MarkHeading>
                        <Step2Audience
                            data={step2}
                            onChange={handleStep2Change}
                            triggerType={step1.triggerType}
                            errors={step2Errors}
                        />
                    </div>
                )}
                {step === 2 && (
                    <div
                        ref={(el) => {
                            stepRefs.current[2] = el;
                        }}
                    >
                        <MarkHeading
                            role="h4"
                            ref={(el) => {
                                stepHeadingRefs.current[2] = el;
                            }}
                            tabIndex={-1}
                            style={{
                                margin: '0 0 1rem'
                            }}
                        >
                            Schedule
                        </MarkHeading>
                        <Step3Schedule
                            data={step3}
                            onChange={handleStep3Change}
                            errors={step3Errors}
                        />
                    </div>
                )}
                {step === 3 && (
                    <div
                        ref={(el) => {
                            stepRefs.current[3] = el;
                        }}
                    >
                        <MarkHeading
                            role="h4"
                            ref={(el) => {
                                stepHeadingRefs.current[3] = el;
                            }}
                            tabIndex={-1}
                            style={{
                                margin: '0 0 1rem'
                            }}
                        >
                            Review
                        </MarkHeading>
                        <Step4Review
                            step1={step1}
                            step2={step2}
                            step3={step3}
                            isSubmitting={isSaving}
                            submitError={submitError}
                            onSubmit={handleSubmit}
                        />
                    </div>
                )}
            </fieldset>
            <div className={layout.formFooter}>
                <MarkButton type="button" onClick={goPrev} disabled={step === 0 || isSaving}>
                    Back
                </MarkButton>
                {step < STEPS.length - 1 && (
                    <MarkButton
                        type="button"
                        onClick={goNext}
                        disabled={isSaving || (step === 0 && !step1.name.trim())}
                        variant="primary"
                    >
                        Next
                    </MarkButton>
                )}
            </div>
        </Section>
    );
};
export default NewDrip;
